import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase.js';
import logger from '../../utils/logger';

/**
 * REQ-03 — biography cards stored in profiles/{id}:
 *   type      'founder' | 'team' | 'moderator'
 *   name, title, subjects[], bio, email, phone
 *   photoUrl, photoPath (Storage path when uploaded through the site)
 *   order     display order within its section (lowest first)
 *   visible   shown on /about-us when true
 *   userId    linked account; a moderator may edit their own card
 *
 * Visitors read visible cards; administrators read every card; a signed-in
 * user also reads their own cards even while hidden.
 */
export default function useProfiles({ isAdmin, uid }) {
  const [sources, setSources] = useState({});
  const [loaded, setLoaded] = useState({});

  useEffect(() => {
    const subscriptions = isAdmin
      ? { all: collection(db, 'profiles') }
      : {
          visible: query(collection(db, 'profiles'), where('visible', '==', true)),
          ...(uid ? { own: query(collection(db, 'profiles'), where('userId', '==', uid)) } : {}),
        };

    setSources({});
    setLoaded({});
    const unsubscribes = Object.entries(subscriptions).map(([key, ref]) =>
      onSnapshot(
        ref,
        (snapshot) => {
          setSources((prev) => ({ ...prev, [key]: snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) }));
          setLoaded((prev) => ({ ...prev, [key]: true }));
        },
        (err) => {
          logger.error(`Error loading profiles (${key}):`, err);
          setLoaded((prev) => ({ ...prev, [key]: true }));
        }
      )
    );
    return () => unsubscribes.forEach((u) => u());
  }, [isAdmin, uid]);

  const profiles = useMemo(() => {
    const byId = new Map();
    Object.values(sources).flat().forEach((p) => byId.set(p.id, p));
    return Array.from(byId.values()).sort(
      (a, b) => (a.order ?? 9999) - (b.order ?? 9999) || (a.name || '').localeCompare(b.name || '')
    );
  }, [sources]);

  const expected = isAdmin ? 1 : uid ? 2 : 1;
  return { profiles, loading: Object.keys(loaded).length < expected };
}
